export const SALES_BRIEFING = {
  greeting: {
    date: 'Monday, Jan 27',
    meetings: 3,
    tasks: 4,
    dealAlerts: 2,
    lucMessage: "Hey David, strong week ahead — your pipeline coverage is solid at 3.1x and win rate is trending up. Sally flagged 2 deals that need attention before your prospect call at 11."
  },
  deals: [
    {
      id: 'd1',
      title: 'TechCorp Enterprise — Stalled',
      agent: 'sally',
      status: 'at-risk',
      alert: true,
      value: '$52,000',
      stage: 'Negotiation',
      daysInStage: 14,
      description: 'No response from champion in 12 days. Sally recommends a re-engagement sequence before the deal goes cold.',
      expanded: {
        summary: 'TechCorp was progressing well through the pipeline until the champion (CTO, James Park) went silent after the pricing call. Competitor AgentHub is also in talks with them.',
        details: [
          { label: 'Deal Value', value: '$52,000 / year' },
          { label: 'Stage', value: 'Negotiation (14 days)' },
          { label: 'Champion', value: 'James Park (CTO)' },
          { label: 'Last Contact', value: '12 days ago' },
          { label: 'Competitor', value: 'AgentHub (active)' },
          { label: 'Win Probability', value: '38% (was 65%)' },
        ],
        thinking: 'Sally analyzed communication patterns and CRM data. The silence correlates with AgentHub scheduling a demo last week (sourced from LinkedIn activity). The pricing objection from the last call was never fully resolved. Recommends a value-focused re-engagement rather than a discount offer.',
        proposedActions: [
          { label: 'Send case study from similar company', agent: 'Sally' },
          { label: 'Draft re-engagement email with ROI data', agent: 'Sally' },
          { label: 'Alert Arjun for potential executive outreach', agent: 'Luc' },
        ]
      }
    },
    {
      id: 'd2',
      title: 'DataFlow Series B — Budget Freeze',
      agent: 'sally',
      status: 'at-risk',
      alert: true,
      value: '$38,000',
      stage: 'Proposal Sent',
      daysInStage: 21,
      description: 'Champion confirmed internal budget freeze until March. Deal likely slipping to Q2.',
      expanded: {
        summary: 'DataFlow\'s CFO froze all new vendor spend after a board meeting. The champion (VP Ops, Lisa Chen) is still interested but cannot get sign-off until Q2 budget opens.',
        details: [
          { label: 'Deal Value', value: '$38,000 / year' },
          { label: 'Stage', value: 'Proposal Sent (21 days)' },
          { label: 'Champion', value: 'Lisa Chen (VP Ops)' },
          { label: 'Blocker', value: 'Budget freeze until March' },
          { label: 'Win Probability', value: '45% (was 70%)' },
          { label: 'Q2 Reopen Likelihood', value: 'High (champion committed)' },
        ],
        thinking: 'Sally cross-referenced with Finn\'s intel — DataFlow\'s Series B closed 2 months ago but burn rate concerns triggered the freeze. The champion remains engaged (opened last 3 emails, attended webinar). Recommends keeping warm without pressure.',
        proposedActions: [
          { label: 'Move to Q2 pipeline forecast', agent: 'Sally' },
          { label: 'Schedule monthly check-in with champion', agent: 'Sally' },
          { label: 'Update cashflow projection for Q1 shortfall', agent: 'Finn' },
        ]
      }
    },
    {
      id: 'd3',
      title: 'NovaTech — Closing This Week',
      agent: 'sally',
      status: 'closing',
      alert: false,
      value: '$28,000',
      stage: 'Contract Review',
      daysInStage: 3,
      description: 'Legal review in progress. Champion expects sign-off by Thursday.',
      expanded: {
        summary: 'NovaTech\'s legal team has the contract. Minor redlines on data processing terms. Sally has pre-drafted responses for common objections.',
        details: [
          { label: 'Deal Value', value: '$28,000 / year' },
          { label: 'Stage', value: 'Contract Review (3 days)' },
          { label: 'Champion', value: 'Raj Patel (Head of Ops)' },
          { label: 'Expected Close', value: 'Thursday, Jan 30' },
          { label: 'Win Probability', value: '92%' },
          { label: 'Redlines', value: '2 minor (data processing)' },
        ],
        thinking: 'Sally reviewed similar contract negotiations — the data processing redlines are standard and were resolved in under 24 hours in 4 of the last 5 deals. No blockers expected.',
      }
    }
  ],
  tasks: {
    today: [
      {
        id: 'st1',
        title: 'Prep for NovaTech closing call',
        agent: 'sally',
        urgency: 'time-sensitive',
        description: 'Sally compiled talking points, contract status, and redline responses for the 11 AM call.',
        expanded: {
          summary: 'Sally has prepared a deal brief covering the 2 outstanding redlines, competitive positioning notes, and a suggested close approach.',
          artifacts: [
            { name: 'Deal Brief', type: 'pdf' },
            { name: 'Redline Responses', type: 'pdf' },
          ],
          thinking: 'Sally analyzed the champion\'s communication style and previous objection patterns. Direct, ROI-focused closing works best with this buyer persona. The 2 redlines are standard — recommend conceding on clause 4.2 (data retention) to accelerate close.'
        }
      },
      {
        id: 'st2',
        title: 'Review updated Q1 forecast',
        agent: 'sally',
        urgency: 'due-today',
        description: 'Sally adjusted projections after TechCorp and DataFlow status changes. Revised to $342K.',
        expanded: {
          summary: 'Q1 forecast revised down from $380K to $342K due to 2 enterprise deals slipping. SMB pipeline compensates partially.',
          details: [
            { label: 'Original Forecast', value: '$380,000' },
            { label: 'Revised Forecast', value: '$342,000' },
            { label: 'Deals Slipped', value: '2 ($90K combined)' },
            { label: 'SMB Compensation', value: '+$22K above target' },
            { label: 'Pipeline Coverage', value: '3.1x (healthy)' },
          ],
          artifacts: [
            { name: 'Q1 Forecast Model', type: 'spreadsheet' },
          ]
        }
      },
      {
        id: 'st3',
        title: 'Follow up with Meridian Health',
        agent: 'sally',
        description: 'Demo completed last week. Sally drafted a follow-up email with a custom proposal outline.',
        expanded: {
          summary: 'Meridian Health attended the demo on Thursday. Strong interest from VP Operations. Sally recommends sending a tailored proposal within 48 hours to maintain momentum.',
          details: [
            { label: 'Prospect', value: 'Meridian Health (Series A, 85 employees)' },
            { label: 'Champion', value: 'VP Operations — Anita Desai' },
            { label: 'Deal Size (est)', value: '$24,000 / year' },
            { label: 'Interest Level', value: 'High (asked about implementation)' },
          ],
          artifacts: [
            { name: 'Follow-up Email Draft', type: 'pdf' },
            { name: 'Proposal Outline', type: 'pdf' },
          ]
        }
      }
    ],
    ifTime: [
      { id: 'st4', title: 'CRM hygiene — 8 stale opportunities', agent: 'sally', description: 'Sally identified 8 deals with no activity in 30+ days. Recommend archiving or re-engaging.' },
      { id: 'st5', title: 'Review team coaching notes', agent: 'dora', description: 'Dora compiled call analysis from 12 rep conversations this week. Key coaching moments flagged.' }
    ]
  },
  pipelineMetrics: {
    snapshot: [
      { label: 'Q1 Forecast', value: '$342K', trend: '-10%', alert: true },
      { label: 'Pipeline Coverage', value: '3.1x' },
      { label: 'Win Rate (30d)', value: '34%', trend: '+6%' },
      { label: 'Avg Deal Cycle', value: '28 days', trend: '-3 days' },
      { label: 'Deals Closed (MTD)', value: '7', trend: '+2' },
    ],
    alert: {
      id: 'pa1',
      title: '2 enterprise deals at risk of slipping to Q2',
      agents: ['sally', 'finn'],
      severity: 'high',
      description: 'TechCorp ($52K) and DataFlow ($38K) unlikely to close in Q1. Combined pipeline impact: $90K.',
      expanded: {
        details: [
          { label: 'At-Risk Pipeline', value: '$90,000 (2 deals)' },
          { label: 'Q1 Impact', value: 'Forecast down 10% to $342K' },
          { label: 'TechCorp Blocker', value: 'Champion gone silent, competitor active' },
          { label: 'DataFlow Blocker', value: 'Budget freeze until March' },
          { label: 'Mitigation', value: 'SMB pipeline +22% above target' },
        ],
        thinking: 'Sally and Finn jointly assessed the Q1 risk. The $90K shortfall is partially offset by stronger-than-expected SMB momentum (+$22K). Net gap is $48K. Finn has adjusted cashflow projections. Two mitigation paths: (1) accelerate 3 SMB deals in late-stage, (2) executive outreach on TechCorp.',
        proposedActions: [
          { label: 'Accelerate 3 late-stage SMB deals', agent: 'Sally' },
          { label: 'Request CEO outreach to TechCorp', agent: 'Luc' },
          { label: 'Adjust Q1 cashflow model', agent: 'Finn' },
        ]
      }
    }
  },
  meetings: [
    { id: 'sm1', title: 'NovaTech closing call', time: '11:00 AM', duration: '30 min', with: 'Raj Patel (NovaTech)', type: 'external', agenda: ['Contract redline resolution', 'Implementation timeline', 'Close and next steps'] },
    { id: 'sm2', title: 'Pipeline review', time: '1:00 PM', duration: '30 min', with: 'Sales Team', type: 'internal', agenda: ['Q1 forecast update', 'At-risk deals review', 'SMB acceleration plan'] },
    { id: 'sm3', title: 'Forecast sync with Arjun', time: '3:30 PM', duration: '30 min', with: 'Arjun Mehta (CEO)', type: 'internal', agenda: ['Revised Q1 numbers', 'TechCorp executive outreach request', 'Q2 pipeline outlook'] },
  ],
  needsDecision: [
    {
      id: 'dnd1',
      agent: 'sally',
      title: 'Request CEO outreach to TechCorp (James Park)',
      description: 'Champion gone silent 12 days, AgentHub circling. Sally recommends Arjun do a value-focused CEO-to-CTO ping before this goes fully cold.',
      amountLabel: 'Deal at stake',
      amount: '$52,000 ARR',
      urgency: 'critical',
      expanded: {
        summary: 'TechCorp was at 65% win probability before silence. Now 38%. The pricing objection from the last call was never fully closed. CEO outreach sends a signal AgentHub can\'t match and gives us a reason to re-open the thread without a hard pitch.',
        details: [
          { label: 'Deal value', value: '$52K ARR' },
          { label: 'Champion', value: 'James Park (CTO)' },
          { label: 'Last contact', value: '12 days ago' },
          { label: 'Competitor activity', value: 'AgentHub demo scheduled' },
          { label: 'Proposed touch', value: '3-line CEO note, no pitch, open door' },
        ],
        thinking: "Sally cross-referenced comparable silent-champion scenarios. In 7 of 9 past deals, a CEO-level value ping re-opened the conversation within 48 hours. The ones that didn't work were already signed with a competitor — which means this is a probe, not a risk.",
      },
    },
    {
      id: 'dnd2',
      agent: 'sally',
      title: 'Concede NovaTech clause 4.2 (90-day data retention)',
      description: "Raj's legal wants 90-day retention vs our standard 180. Sally recommends conceding — it's a standard cleanup that historically resolves in <24 hours.",
      amountLabel: 'Unblocks',
      amount: '$28K close Thursday',
      urgency: 'normal',
      expanded: {
        summary: 'Clause 4.2 sits in 4 of our last 5 closed enterprise deals as a negotiated redline. We conceded each time with no downstream impact. Keeping it open here risks slipping Thursday close into next week for no meaningful protection gain.',
        details: [
          { label: 'Deal value', value: '$28K / year' },
          { label: 'Win probability', value: '92%' },
          { label: 'Expected close', value: 'Thursday, Jan 30' },
          { label: 'Historical precedent', value: 'Conceded 4 / 5 recent deals' },
          { label: 'Risk if held', value: 'Slip to next week, momentum loss' },
        ],
      },
    },
    {
      id: 'dnd3',
      agent: 'sally',
      title: 'Accept DataFlow slip to Q2 + keep-warm plan',
      description: 'Budget freeze until March is confirmed. Lisa Chen remains engaged. Sally recommends removing from Q1 forecast and running a 6-week monthly nurture.',
      amountLabel: 'Q1 forecast impact',
      amount: '-$38,000',
      urgency: 'normal',
      expanded: {
        summary: "Lisa Chen's signal is clear — the interest is real, the budget isn't. Running a low-pressure monthly touchpoint keeps us on her shortlist when budget opens. Forcing a Q1 close only burns the champion.",
        details: [
          { label: 'Deal value', value: '$38K / year' },
          { label: 'Blocker', value: 'Budget freeze until March' },
          { label: 'Q2 reopen likelihood', value: 'High (champion committed)' },
          { label: 'Nurture cadence', value: '1 touchpoint / month, 6 weeks' },
          { label: 'Forecast impact', value: 'Removed from Q1 ($38K)' },
        ],
      },
    },
  ],
  dailyTimeline: [
    { time: '9:00 AM', type: 'task', title: 'Prep for NovaTech closing call', agent: 'sally' },
    { time: '9:30 AM', type: 'task', title: 'Review updated Q1 forecast', agent: 'sally' },
    { time: '10:00 AM', type: 'task', title: 'Follow up with Meridian Health', agent: 'sally' },
    { time: '11:00 AM', type: 'meeting', title: 'NovaTech closing call', duration: '30 min', with: 'Raj Patel (NovaTech)' },
    { time: '1:00 PM', type: 'meeting', title: 'Pipeline review', duration: '30 min', with: 'Sales Team' },
    { time: '3:30 PM', type: 'meeting', title: 'Forecast sync with Arjun', duration: '30 min', with: 'Arjun Mehta (CEO)' },
  ],
  inbox: [
    { id: 'sli1', sender: 'Raj Patel (NovaTech)', senderInitials: 'RP', time: '8:20 AM', summary: 'Legal has 2 minor redlines on data processing — expects sign-off by Thursday.', expandedSummary: 'Raj confirmed legal is reviewing the contract. Two redlines: clause 4.2 (data retention period — they want 90 days instead of 180) and clause 7.1 (liability cap language). He expects internal approval by Thursday if we can turn around responses by Wednesday. Tone is positive — he called it "standard cleanup."' },
    { id: 'sli2', sender: 'Anita Desai (Meridian Health)', senderInitials: 'AD', time: '8:00 AM', summary: 'Loved the demo — asking about implementation timeline and team size needed.', expandedSummary: 'Anita followed up after last week\'s demo with specific questions: how long is typical implementation, does she need a dedicated internal resource, and can we do a phased rollout starting with her ops team? She also asked about the pricing tier for 85 employees. Strong buying signals — she\'s already socializing internally.' },
    { id: 'sli3', sender: 'James Park (TechCorp CTO)', senderInitials: 'JP', time: '7:40 AM', summary: 'Frustrated about API issues — considering pausing the evaluation.', expandedSummary: 'James is unhappy about the v2.3 API breakage affecting their staging environment. He mentioned "competitor AgentHub didn\'t have these issues in their POC." He\'s not pulling out yet but wants a call with someone senior and a written timeline for the fix. This needs immediate attention given the $52K deal value and 45-day renewal window.' },
    { id: 'sli4', sender: 'Arjun Mehta (CEO)', senderInitials: 'AM', time: 'Yesterday', summary: 'Wants pipeline update before his 3:30 forecast sync with Neel.', expandedSummary: 'Arjun wants to go into the finance sync prepared. Needs: revised Q1 forecast number, the 2 slipped deals narrative, SMB offset story, and your confidence level on the 3 late-stage SMB deals. Prefers a 2-minute voice note or a bullet-point email by 2 PM.' },
    { id: 'sli5', sender: 'Priya Sharma (Marketing)', senderInitials: 'PS', time: 'Yesterday', summary: 'Case study for TechCorp re-engagement — needs your input on the customer story.', expandedSummary: 'Priya is packaging a case study for the TechCorp outreach. She needs you to confirm: which customer is the best comparison (similar size, API-heavy use case), what ROI metrics to highlight, and a quote from the customer if possible. Deadline is Wednesday to align with Sally\'s re-engagement sequence.' },
  ]
};
